import type {
  CacheInvalidationKey,
  CanvasChangedMessage,
  CanvasResetMessage,
  PopulousActiveTab,
  PopulousStatusUpdateMessage,
  ProjectPresenceUser,
} from "schemas/generated/agentConnection";
import type { BedrockClient } from "../client";
import type { RealtimeConnection } from "../realtime-connection";
import type {
  FilesChangedHandler,
  FilesChangedSubscribeOptions,
} from "./files-changed";

import {
  cacheInvalidationMessageSchema,
  canvasChangedMessageSchema,
  canvasResetMessageSchema,
  projectPresenceMessageSchema,
} from "schemas/agentConnection";

import { subscribeToFilesChanged } from "./files-changed";

type CollaborationMessageHandler = (message: Record<string, unknown>) => void;

// (Berk, REPL-30383): Bare heartbeats keep the Populous map's lastSeenAt fresh so readers can age out ghosts; they never carry a location.
const POPULOUS_HEARTBEAT_MS = 60_000;

function isSameActiveTab(
  previous: PopulousActiveTab,
  next: PopulousActiveTab,
): boolean {
  if (previous.tabType !== next.tabType) {
    return false;
  }
  if (previous.tabType === "miniapp" && next.tabType === "miniapp") {
    return (
      previous.miniappId === next.miniappId &&
      previous.miniappRoute === next.miniappRoute
    );
  }
  return true;
}

/**
 * Pure-TS handle to the project-coordinator DO: the project-scoped collaboration
 * channel (project-wide `files-changed` fan-out, and later presence), disjoint
 * from the agent/compute socket on `client.agent`. It mints its own token via
 * the client, so React and non-React consumers share one path.
 */
export class ProjectCoordinator {
  private connection: RealtimeConnection | null = null;
  private readonly messageHandlers = new Set<CollaborationMessageHandler>();
  private lastActiveTab: PopulousActiveTab | undefined;
  private populousHeartbeat: ReturnType<typeof setInterval> | null = null;

  constructor(private readonly client: BedrockClient) {}

  connect() {
    this.connection = this.client.connectProjectCoordinator({
      onRawMessage: (message) => this.handleMessage(message),
      // Re-assert location, not just liveness: the map forgets rows it stops
      // hearing from, and the dedupe below would suppress the tab forever.
      onOpen: () => this.sendStatus(this.lastActiveTab),
    });
    this.populousHeartbeat = setInterval(
      () => this.sendStatus(),
      POPULOUS_HEARTBEAT_MS,
    );
  }

  close() {
    if (this.populousHeartbeat) {
      clearInterval(this.populousHeartbeat);
      this.populousHeartbeat = null;
    }
    this.connection?.close();
    this.connection = null;
  }

  /**
   * Publish which tab this client is viewing on the global Populous map.
   * Callers only invoke this from a foregrounded page for a real tab change;
   * duplicates are dropped so re-renders and refocuses cost nothing.
   */
  updateActiveTab(activeTab: PopulousActiveTab) {
    if (this.lastActiveTab && isSameActiveTab(this.lastActiveTab, activeTab)) {
      return;
    }
    this.lastActiveTab = activeTab;
    this.sendStatus(activeTab);
  }

  /** No `activeTab` is a bare heartbeat: liveness without a location. */
  private sendStatus(activeTab?: PopulousActiveTab) {
    this.connection?.send({
      type: "populous-status-update",
      activeTab,
    } satisfies PopulousStatusUpdateMessage);
  }

  private handleMessage(message: unknown) {
    if (typeof message !== "object" || message === null) {
      return;
    }
    // eslint-disable-next-line replo/no-as-cast -- parsed JSON object; downstream subscribers validate with Zod.
    const msg = message as Record<string, unknown>;
    for (const handler of this.messageHandlers) {
      handler(msg);
    }
  }

  onMessage(handler: CollaborationMessageHandler): () => void {
    this.messageHandlers.add(handler);
    return () => {
      this.messageHandlers.delete(handler);
    };
  }

  // (Max, 2026-07-02, REPL-28703): Sugar over `onMessage` for the DO's
  // `project-presence` roster broadcasts. Identity is resolved server-side from
  // the verified upgrade; the only client-originated frame is the cosmetic
  // `populous-status-update` sent by `updateActiveTab` / the heartbeat.
  /** Query keys bedrock says are stale for this project (workflow runs today). */
  onCacheInvalidation(
    handler: (keys: CacheInvalidationKey[]) => void,
  ): () => void {
    return this.onMessage((message) => {
      const parsed = cacheInvalidationMessageSchema.safeParse(message);
      if (parsed.success) {
        handler(parsed.data.keys);
      }
    });
  }

  onPresence(handler: (users: ProjectPresenceUser[]) => void): () => void {
    return this.onMessage((message) => {
      const parsed = projectPresenceMessageSchema.safeParse(message);
      if (parsed.success) {
        handler(parsed.data.users);
      }
    });
  }

  /** Operation batches the coordinator applied to a Canvas set, in server order. */
  onCanvasChanged(
    handler: (message: CanvasChangedMessage) => void,
  ): () => void {
    return this.onMessage((message) => {
      const parsed = canvasChangedMessageSchema.safeParse(message);
      if (parsed.success) {
        handler(parsed.data);
      }
    });
  }

  /** The coordinator dropped its copy of a Canvas set; editors re-open it. */
  onCanvasReset(handler: (message: CanvasResetMessage) => void): () => void {
    return this.onMessage((message) => {
      const parsed = canvasResetMessageSchema.safeParse(message);
      if (parsed.success) {
        handler(parsed.data);
      }
    });
  }

  // Sugar over `onMessage` for the project's control-plane `files-changed`
  // events (bedrock-api is the Drive authority). Consumer-side glob filtering
  // and throttling live in `subscribeToFilesChanged`.
  onFilesChanged({
    options,
    handler,
  }: {
    options: FilesChangedSubscribeOptions;
    handler: FilesChangedHandler;
  }): () => void {
    return subscribeToFilesChanged({
      onMessage: (innerHandler) => this.onMessage(innerHandler),
      options,
      handler,
    });
  }
}
